import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Factory, Plus, Search, MapPin, Phone, Mail, User, Gauge, ShieldCheck,
  Pencil, Trash2, Save, Copy, X, RefreshCw, KeyRound, ExternalLink,
} from "lucide-react";

const emptyForm = {
  code: "",
  name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  gstin: "",
  phone: "",
  email: "",
  in_charge_name: "",
  in_charge_phone: "",
  licence_number: "",
  fssai_number: "",
  daily_capacity_units: "",
  capacity_unit: "pcs/day",
  notes: "",
  is_active: true,
  auto_create_admin: false,
  auto_create_role: "factory_admin",
  admin_name: "",
  admin_email: "",
};

export default function FactoriesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...factory} = edit
  const [newCredential, setNewCredential] = useState(null); // {email, password, factory_name}

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (showActiveOnly) params.set("active", "true");
      const r = await api.get(`/factories${params.toString() ? `?${params.toString()}` : ""}`);
      setRows(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load factories");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showActiveOnly]);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => r.is_active !== false).length,
    admins: rows.reduce((s, r) => s + (r.admin_count || 0), 0),
    capacity: rows.reduce((s, r) => s + (Number(r.daily_capacity_units) || 0), 0),
  }), [rows]);

  const toggleActive = async (f) => {
    try {
      await api.patch(`/factories/${f.id}`, { is_active: !f.is_active });
      toast.success(`${f.name} ${!f.is_active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/factories/${f.id}`);
      toast.success("Factory deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot delete"); }
  };

  return (
    <div className="space-y-6" data-testid="factories-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Operations · Factory Master</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Factories</h1>
          <p className="text-sm text-[#8B7355] mt-1">
            Set up and manage each manufacturing plant — address, capacity, licences, and its own admin login.
            Super Admin only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="fct-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={() => setEditing({})}
            className="bg-[#C05621] text-white hover:bg-[#a04519]"
            data-testid="fct-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New Factory
          </Button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total Factories" value={stats.total} tone="neutral" icon={Factory} />
        <StatTile label="Active" value={stats.active} tone="emerald" icon={ShieldCheck} />
        <StatTile label="Factory Admins" value={stats.admins} tone="blue" icon={User} />
        <StatTile label="Total Capacity" value={`${stats.capacity.toLocaleString()} /day`} tone="amber" icon={Gauge} />
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => { e.preventDefault(); load(); }}
        className="flex flex-wrap items-end gap-2 p-3 bg-white border border-[#EAE5D9] rounded-md"
      >
        <div className="flex-1 min-w-[240px]">
          <Label className="text-[10px] uppercase text-[#8B7355]">Search</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#8B7355]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, code, or city…"
              className="pl-7"
              data-testid="fct-search"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer px-2 py-2">
          <Switch checked={showActiveOnly} onCheckedChange={setShowActiveOnly} data-testid="fct-active-only" />
          <span>Active only</span>
        </label>
        <Button type="submit" variant="outline" size="sm"><Search className="w-3.5 h-3.5 mr-1" /> Search</Button>
      </form>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fct-grid">
        {rows.map((f) => (
          <FactoryCard
            key={f.id}
            f={f}
            onEdit={() => setEditing(f)}
            onToggle={() => toggleActive(f)}
            onDelete={() => remove(f)}
          />
        ))}
        {rows.length === 0 && !loading && (
          <div className="col-span-full text-center py-16 bg-white border border-dashed border-[#EAE5D9] rounded-lg">
            <Factory className="w-10 h-10 mx-auto text-[#8B7355] opacity-40 mb-2" />
            <div className="text-sm text-[#8B7355]">
              No factories yet. Click <b>New Factory</b> to add your first plant.
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <FactoryFormDialog
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={(res) => {
            setEditing(null);
            load();
            // If a new admin was auto-provisioned, show credentials modal
            if (res?.admin_creation?.created) {
              setNewCredential({
                factory_name: res.factory?.name,
                email: res.admin_creation.email,
                password: res.admin_creation.temp_password,
                name: res.admin_creation.name,
                role: res.admin_creation.role,
              });
            } else if (res?.admin_creation && !res.admin_creation.created) {
              toast.info(res.admin_creation.reason);
            }
          }}
        />
      )}

      {newCredential && (
        <NewAdminCredsDialog
          creds={newCredential}
          onClose={() => setNewCredential(null)}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, tone, icon: Icon }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    neutral: "bg-white border-[#EAE5D9]",
  }[tone];
  return (
    <div className={`border rounded-lg p-3 ${bg}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-2xl font-[Outfit] font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function FactoryCard({ f, onEdit, onToggle, onDelete }) {
  return (
    <div
      className={`bg-white border rounded-lg overflow-hidden ${f.is_active ? "border-[#EAE5D9]" : "border-slate-200 opacity-70"}`}
      data-testid={`fct-card-${f.id}`}
    >
      <div className="p-4 border-b border-[#EAE5D9] bg-gradient-to-br from-[#FEF9EA] to-white">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#8B7355]">{f.code}</div>
            <div className="font-[Outfit] text-lg font-semibold truncate" data-testid={`fct-name-${f.id}`}>
              {f.name}
            </div>
          </div>
          {f.is_active ? (
            <Badge className="border-0 bg-emerald-500 text-white">Active</Badge>
          ) : (
            <Badge className="border-0 bg-slate-400 text-white">Inactive</Badge>
          )}
        </div>
      </div>

      <div className="p-4 space-y-2 text-xs">
        {(f.city || f.state) && (
          <div className="flex items-start gap-2 text-[#5C4033]">
            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <div className="truncate">{[f.address_line1, f.city, f.state].filter(Boolean).join(" · ")}</div>
          </div>
        )}
        {f.in_charge_name && (
          <div className="flex items-center gap-2 text-[#5C4033]">
            <User className="w-3 h-3 flex-shrink-0" />
            <div className="truncate">
              {f.in_charge_name}{f.in_charge_phone ? ` · ${f.in_charge_phone}` : ""}
            </div>
          </div>
        )}
        {f.daily_capacity_units && (
          <div className="flex items-center gap-2 text-[#5C4033]">
            <Gauge className="w-3 h-3 flex-shrink-0" />
            <div>{Number(f.daily_capacity_units).toLocaleString()} {f.capacity_unit || "units"}</div>
          </div>
        )}
        <div className="flex items-center gap-4 pt-2 text-[10px] text-[#8B7355] border-t border-dashed border-[#EAE5D9]">
          <span><b className="text-[#5C4033]">{f.admin_count}</b> admins</span>
          <span><b className="text-[#5C4033]">{f.plans_today}</b> plans today</span>
          <span><b className="text-[#5C4033]">{f.active_orders}</b> active</span>
        </div>
      </div>

      <div className="p-3 pt-0 flex gap-1.5">
        <Button size="sm" variant="outline" className="flex-1" onClick={onEdit} data-testid={`fct-edit-${f.id}`}>
          <Pencil className="w-3 h-3 mr-1" /> Edit
        </Button>
        <Button size="sm" variant="outline" onClick={onToggle} data-testid={`fct-toggle-${f.id}`}>
          {f.is_active ? "Deactivate" : "Activate"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onDelete}
          className="text-red-600 hover:bg-red-50 hover:border-red-300"
          data-testid={`fct-delete-${f.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// ==================== Create / Edit Dialog ====================
function FactoryFormDialog({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => (isEdit ? { ...emptyForm, ...initial, auto_create_admin: false } : { ...emptyForm }));
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      toast.error("Code and name are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        address_line1: form.address_line1 || null,
        address_line2: form.address_line2 || null,
        city: form.city || null,
        state: form.state || null,
        pincode: form.pincode || null,
        gstin: form.gstin || null,
        phone: form.phone || null,
        email: form.email || null,
        in_charge_name: form.in_charge_name || null,
        in_charge_phone: form.in_charge_phone || null,
        licence_number: form.licence_number || null,
        fssai_number: form.fssai_number || null,
        daily_capacity_units: form.daily_capacity_units ? Number(form.daily_capacity_units) : null,
        capacity_unit: form.capacity_unit || null,
        notes: form.notes || null,
        is_active: !!form.is_active,
      };
      if (isEdit) {
        const r = await api.patch(`/factories/${initial.id}`, payload);
        toast.success("Saved");
        onSaved({ factory: r.data });
      } else {
        // Include admin auto-create fields
        payload.auto_create_admin = !!form.auto_create_admin;
        payload.auto_create_role = form.auto_create_role || "factory_admin";
        payload.admin_name = form.admin_name || undefined;
        payload.admin_email = form.admin_email || undefined;
        const r = await api.post("/factories", payload);
        toast.success("Factory created");
        onSaved(r.data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="fct-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <Factory className="w-5 h-5" /> {isEdit ? `Edit — ${initial.name}` : "New Factory"}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[#8B7355]">
            Set up the factory profile. All fields except <b>Code</b> and <b>Name</b> are optional and can be filled later.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <div>
            <Label>Factory Code*</Label>
            <Input value={form.code} onChange={(e) => set({ code: e.target.value })} placeholder="FCT3" disabled={isEdit && !!initial.code} data-testid="fct-form-code" />
          </div>
          <div>
            <Label>Factory Name*</Label>
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Factory 3 — Bangalore" data-testid="fct-form-name" />
          </div>

          <div className="col-span-2"><div className="uc-label !text-[#5C4033] mt-3">Address</div></div>
          <div className="col-span-2">
            <Label>Address line 1</Label>
            <Input value={form.address_line1 || ""} onChange={(e) => set({ address_line1: e.target.value })} data-testid="fct-form-addr1" />
          </div>
          <div className="col-span-2">
            <Label>Address line 2</Label>
            <Input value={form.address_line2 || ""} onChange={(e) => set({ address_line2: e.target.value })} data-testid="fct-form-addr2" />
          </div>
          <div><Label>City</Label><Input value={form.city || ""} onChange={(e) => set({ city: e.target.value })} data-testid="fct-form-city" /></div>
          <div><Label>State</Label><Input value={form.state || ""} onChange={(e) => set({ state: e.target.value })} data-testid="fct-form-state" /></div>
          <div><Label>Pincode</Label><Input value={form.pincode || ""} onChange={(e) => set({ pincode: e.target.value })} data-testid="fct-form-pincode" /></div>
          <div><Label>GSTIN</Label><Input value={form.gstin || ""} onChange={(e) => set({ gstin: e.target.value })} data-testid="fct-form-gstin" /></div>

          <div className="col-span-2"><div className="uc-label !text-[#5C4033] mt-3">Contact</div></div>
          <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => set({ phone: e.target.value })} data-testid="fct-form-phone" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => set({ email: e.target.value })} data-testid="fct-form-email" /></div>
          <div><Label>In-charge name</Label><Input value={form.in_charge_name || ""} onChange={(e) => set({ in_charge_name: e.target.value })} data-testid="fct-form-incharge" /></div>
          <div><Label>In-charge phone</Label><Input value={form.in_charge_phone || ""} onChange={(e) => set({ in_charge_phone: e.target.value })} data-testid="fct-form-incharge-phone" /></div>

          <div className="col-span-2"><div className="uc-label !text-[#5C4033] mt-3">Licences & Capacity</div></div>
          <div><Label>Factory Licence No.</Label><Input value={form.licence_number || ""} onChange={(e) => set({ licence_number: e.target.value })} data-testid="fct-form-licence" /></div>
          <div><Label>FSSAI Number</Label><Input value={form.fssai_number || ""} onChange={(e) => set({ fssai_number: e.target.value })} data-testid="fct-form-fssai" /></div>
          <div>
            <Label>Daily Capacity</Label>
            <Input
              type="number" min="0" step="1"
              value={form.daily_capacity_units || ""}
              onChange={(e) => set({ daily_capacity_units: e.target.value })}
              data-testid="fct-form-capacity"
            />
          </div>
          <div>
            <Label>Capacity Unit</Label>
            <Input
              value={form.capacity_unit || ""}
              onChange={(e) => set({ capacity_unit: e.target.value })}
              placeholder="pcs/day, kg/day…"
              data-testid="fct-form-capacity-unit"
            />
          </div>

          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="fct-form-notes" />
          </div>

          <div className="col-span-2 flex items-center gap-3 bg-[#FEF9EA] border border-amber-200 rounded p-3">
            <Switch checked={!!form.is_active} onCheckedChange={(v) => set({ is_active: v })} data-testid="fct-form-active" />
            <div className="text-sm">{form.is_active ? "Factory is active" : "Factory is inactive"}</div>
          </div>

          {!isEdit && (
            <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded p-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={!!form.auto_create_admin}
                  onCheckedChange={(v) => set({ auto_create_admin: v })}
                  data-testid="fct-form-auto-admin"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm flex items-center gap-1">
                    <KeyRound className="w-3.5 h-3.5" /> Auto-create a factory login
                  </div>
                  <div className="text-[11px] text-[#5C4033]">
                    Provisions a login scoped to this factory. Temporary password shown once after creation;
                    user is forced to change it on first login.
                  </div>
                </div>
              </label>
              {form.auto_create_admin && (
                <div className="grid grid-cols-2 gap-2 pt-1 pl-9">
                  <div className="col-span-2">
                    <Label className="text-[10px]">Role</Label>
                    <Select
                      value={form.auto_create_role || "factory_admin"}
                      onValueChange={(v) => set({ auto_create_role: v })}
                    >
                      <SelectTrigger data-testid="fct-form-auto-role"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="factory_admin">Factory Admin — full access to this unit</SelectItem>
                        <SelectItem value="factory_manager">Factory Manager — ops only (no user or settings)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    placeholder="Display name (optional)"
                    value={form.admin_name}
                    onChange={(e) => set({ admin_name: e.target.value })}
                    data-testid="fct-form-admin-name"
                  />
                  <Input
                    type="email"
                    placeholder={`Email (optional — defaults to ${(form.auto_create_role === "factory_manager") ? "manager" : "admin"}.<code>@bakemill.com)`}
                    value={form.admin_email}
                    onChange={(e) => set({ admin_email: e.target.value })}
                    data-testid="fct-form-admin-email"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={busy}
            className="bg-[#2C1810] text-white"
            data-testid="fct-form-save"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> {isEdit ? "Save changes" : "Create Factory"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Show new admin credentials once ====================
function NewAdminCredsDialog({ creds, onClose }) {
  const copy = (val, label) => {
    navigator.clipboard?.writeText(val);
    toast.success(`${label} copied`);
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="fct-new-admin-creds">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2 text-emerald-800">
            <ShieldCheck className="w-5 h-5" /> {creds.role === "factory_manager" ? "Factory Manager Ready" : "Factory Admin Ready"}
          </DialogTitle>
          <DialogDescription>
            A login for <b>{creds.factory_name}</b> has been created. Share these credentials securely — the temporary
            password will not be shown again. The {creds.role === "factory_manager" ? "manager" : "admin"} will be prompted to change it on first login.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Email</Label>
            <div className="flex items-center gap-1">
              <Input value={creds.email} readOnly className="font-mono text-sm" data-testid="fct-creds-email" />
              <Button size="sm" variant="outline" onClick={() => copy(creds.email, "Email")}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Temporary Password</Label>
            <div className="flex items-center gap-1">
              <Input value={creds.password} readOnly className="font-mono text-sm" data-testid="fct-creds-password" />
              <Button size="sm" variant="outline" onClick={() => copy(creds.password, "Password")}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              Store these somewhere safe (password manager or a secure message). This is the only time the temporary
              password is displayed. If lost, reset it from <a href="/app/factory-admins" className="underline">Factory Admins</a>.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={onClose}
            className="bg-[#2C1810] text-white"
            data-testid="fct-creds-close"
          >
            <X className="w-3.5 h-3.5 mr-1" /> I've saved these
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
